'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export interface FilmCardData {
  id: string;
  title: string;
  year: number;
  runtime: string;
  type: 'short' | 'feature' | 'documentary' | 'animation' | 'series';
  poster: string;
  posterAlt: string;
  genres: string[];
  festivalBadge?: string;
  progressPercent?: number;
  resumeSeconds?: number;
  rating?: string;
  isNew?: boolean;
  isStaffPick?: boolean;
}

interface FilmCardProps {
  film: FilmCardData;
  size?: 'sm' | 'md' | 'lg';
  inWatchlist?: boolean;
  onToggleWatchlist?: (filmId: string) => void;
}

const typeColors: Record<string, string> = {
  short: 'badge-type-short',
  feature: 'badge-type-feature',
  documentary: 'badge-type-doc',
  animation: 'badge-type-short',
  series: 'badge-type-feature',
};

export default function FilmCard({ film, size = 'md', inWatchlist, onToggleWatchlist }: FilmCardProps) {
  const [hovered, setHovered] = useState(false);
  // Local optimistic state when no external control provided
  const [localListed, setLocalListed] = useState(false);

  const listed = inWatchlist !== undefined ? inWatchlist : localListed;

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onToggleWatchlist) {
      onToggleWatchlist(film.id);
    } else {
      setLocalListed(v => !v);
    }
  };

  // If resumeSeconds is set, link directly to the watch page at that timestamp
  const filmHref =
    film.resumeSeconds && film.resumeSeconds > 0
      ? `/watch?id=${film.id}&t=${film.resumeSeconds}`
      : `/film-detail-page?id=${film.id}`;

  // Tighter widths on mobile, expand on sm+
  const widthClass =
    size === 'sm' ?'w-28 sm:w-32 md:w-36'
      : size === 'lg' ?'w-40 sm:w-48 md:w-56 lg:w-60' :'w-32 sm:w-40 md:w-44 lg:w-48';

  return (
    <div
      className={`${widthClass} shrink-0 relative group cursor-pointer film-card-hover`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link href={filmHref}>
        <div className="relative rounded-lg overflow-hidden bg-muted aspect-[2/3]">
          <AppImage
            src={film.poster}
            alt={film.posterAlt}
            fill
            sizes="(max-width: 480px) 112px, (max-width: 640px) 128px, 192px"
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

          {/* Top badges */}
          <div className="absolute top-1.5 left-1.5 flex flex-col gap-1">
            {film.isNew && (
              <span className="text-[10px] font-bold text-primary bg-primary/20 px-1 py-0.5 rounded">NEW</span>
            )}
            {film.festivalBadge && (
              <span className="laurel-badge text-[9px] sm:text-[10px]">🏆 {film.festivalBadge}</span>
            )}
          </div>

          {/* My List button — always visible on touch, hover on desktop */}
          <button
            onClick={handleToggle}
            className={`absolute top-1.5 right-1.5 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center transition-all duration-150 ${
              hovered || listed ? 'opacity-100' : 'opacity-0 sm:opacity-0'
            } ${listed ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white hover:bg-white/20'}`}
            aria-label={listed ? 'Remove from My List' : 'Add to My List'}
          >
            <Icon name={listed ? 'CheckIcon' : 'PlusIcon'} size={12} />
          </button>

          {/* Play button on hover */}
          <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${hovered ? 'opacity-100' : 'opacity-0'}`}>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/90 flex items-center justify-center shadow-lg">
              <Icon name="PlayIcon" size={18} className="text-primary-foreground ml-0.5" />
            </div>
          </div>

          {/* Progress bar */}
          {film.progressPercent !== undefined && film.progressPercent > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
              <div
                className="h-full bg-primary"
                style={{ width: `${film.progressPercent}%` }}
              />
            </div>
          )}

          {/* Runtime badge */}
          <div className="absolute bottom-1.5 left-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <span className="text-[10px] font-mono text-white/80 bg-black/50 px-1 py-0.5 rounded">
              {film.runtime}
            </span>
          </div>
        </div>
      </Link>

      {/* Card info */}
      <div className="mt-1.5 sm:mt-2 px-0.5">
        <Link href={filmHref}>
          <h3 className="text-xs sm:text-sm font-semibold text-foreground leading-tight line-clamp-1 hover:text-primary transition-colors">
            {film.title}
          </h3>
        </Link>
        <div className="flex items-center gap-1 sm:gap-1.5 mt-0.5 flex-wrap">
          <span className="text-[10px] sm:text-xs text-muted-foreground font-tabular">{film.year}</span>
          <span className="text-muted-foreground text-[10px]">·</span>
          <span className={`text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded font-medium ${typeColors[film.type] || 'badge-type-short'}`}>
            {film.type}
          </span>
        </div>
      </div>
    </div>
  );
}